import {
  Alert,
  Checkbox,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Radio,
  Select,
  Slider,
  Switch,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { accountStore } from 'store/accountStore';
import RenderPreviewPane from './RenderPreviewPane';
import {
  RENDER_PRESET_COLOR_OPTIONS,
  RENDER_PRESET_CROP_POSITION_OPTIONS,
  RENDER_PRESET_CROP_RATIO_OPTIONS,
  RENDER_PRESET_DEFAULTS,
  RENDER_PRESET_FIT_MODE_OPTIONS,
  RENDER_PRESET_FIT_OUTPUT_SIZE,
  RENDER_PRESET_FONT_OPTIONS,
  RENDER_PRESET_MARGIN_MAX,
  RENDER_PRESET_MARGIN_MIN,
  RENDER_PRESET_OPACITY_MAX,
  RENDER_PRESET_OPACITY_MIN,
  RENDER_PRESET_OUTPUT_SIZES,
  RENDER_PRESET_SIZE_MAX,
  RENDER_PRESET_SIZE_MIN,
} from '../constants';
import {
  extractPresetFieldErrors,
  formatPresetErrorMessage,
  shortsRenderPresetService,
} from '../services/shortsRenderPresetService';
import {
  RenderPresetColor,
  RenderPresetCropPosition,
  RenderPresetCropRatio,
  RenderPresetFitMode,
  RenderPresetFont,
  RenderPresetSubtitleBackground,
  RenderPreviewRequest,
  ShortsRenderPresetInterface,
  ShortsRenderPresetRequest,
} from '../types/shortsRenderPresetType';

interface ShortsRenderPresetFormModalProps {
  open: boolean;
  /** null → create mode, otherwise edit mode. */
  preset: ShortsRenderPresetInterface | null;
  onClose: () => void;
  onSaved: () => void;
}

interface RenderPresetFormValues {
  name: string;
  font: RenderPresetFont;
  font_color: RenderPresetColor;
  bg_color: RenderPresetColor;
  size_episode: number;
  size_date: number;
  size_program: number;
  size_subtitle: number;
  subtitle_background: RenderPresetSubtitleBackground;
  subtitle_bg_opacity: number;
  fit_mode: RenderPresetFitMode;
  crop_ratio?: RenderPresetCropRatio | null;
  crop_position: RenderPresetCropPosition;
  show_episode_name: boolean;
  show_on_air_date: boolean;
  show_program_name: boolean;
  top_margin: number;
  bottom_margin: number;
  description?: string;
}

/** Form fields that can carry a server-side field error. */
const FORM_FIELD_NAMES: (keyof RenderPresetFormValues)[] = [
  'name',
  'font',
  'font_color',
  'bg_color',
  'size_episode',
  'size_date',
  'size_program',
  'size_subtitle',
  'subtitle_background',
  'subtitle_bg_opacity',
  'fit_mode',
  'crop_ratio',
  'crop_position',
  'show_episode_name',
  'show_on_air_date',
  'show_program_name',
  'top_margin',
  'bottom_margin',
  'description',
];

const isFormFieldName = (field: string): field is keyof RenderPresetFormValues =>
  (FORM_FIELD_NAMES as string[]).includes(field);

/**
 * `subtitle_background` 는 서버에서 enum 이지만 값이 둘뿐이라 운영자에겐 체크박스가 맞다.
 * Form.Item 이 주는 value/onChange 를 enum ↔ boolean 으로 바꿔 끼운다.
 */
const SubtitleBackgroundToggle: React.FC<{
  value?: RenderPresetSubtitleBackground;
  onChange?: (value: RenderPresetSubtitleBackground) => void;
}> = ({ value, onChange }) => (
  <Checkbox
    checked={value === 'box'}
    onChange={e => onChange?.(e.target.checked ? 'box' : 'outline')}
  >
    글자를 감싸는 배경 박스 (해제 시 외곽선)
  </Checkbox>
);

/** 미리보기에 쓸 콘텐츠 ID. 프리셋 화면엔 콘텐츠 맥락이 없어 매번 다시 치게 하지 않는다. */
const PREVIEW_CONTENT_STORAGE_KEY = 'shorts-render-preset:preview-content-id';

const readStoredPreviewContentId = (): number | null => {
  try {
    const raw = window.localStorage.getItem(PREVIEW_CONTENT_STORAGE_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch {
    // Safari private mode 등 localStorage 가 막힌 환경 — 기억만 못 할 뿐 동작엔 지장 없다.
    return null;
  }
};

const SIZE_FIELDS: { name: keyof RenderPresetFormValues; label: string }[] = [
  { name: 'size_episode', label: 'Episode name (px)' },
  { name: 'size_date', label: 'On-air date (px)' },
  { name: 'size_program', label: 'Program name (px)' },
  { name: 'size_subtitle', label: 'Subtitle (px)' },
];

const ShortsRenderPresetFormModal: React.FC<ShortsRenderPresetFormModalProps> = ({
  open,
  preset,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm<RenderPresetFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const isEdit = preset !== null;

  const [previewContentId, setPreviewContentId] = useState<number | null>(null);

  // Watch every field: the preview redraws from the *unsaved* values, so it has to see
  // each edit. The crop-only fields and the output-size hint read from the same object.
  const watched = Form.useWatch([], form) as Partial<RenderPresetFormValues> | undefined;
  const fitMode = watched?.fit_mode;
  const cropRatio = watched?.crop_ratio;
  const isCrop = fitMode === 'crop';
  const hasSubtitleBox = watched?.subtitle_background === 'box';

  useEffect(() => {
    if (!open) {
      return;
    }
    if (preset) {
      form.setFieldsValue({
        name: preset.name,
        font: preset.font,
        font_color: preset.font_color,
        bg_color: preset.bg_color,
        size_episode: preset.size_episode,
        size_date: preset.size_date,
        size_program: preset.size_program,
        size_subtitle: preset.size_subtitle,
        subtitle_background: preset.subtitle_background,
        subtitle_bg_opacity: preset.subtitle_bg_opacity,
        fit_mode: preset.fit_mode,
        crop_ratio: preset.crop_ratio ?? undefined,
        crop_position: preset.crop_position,
        show_episode_name: preset.show_episode_name,
        show_on_air_date: preset.show_on_air_date,
        show_program_name: preset.show_program_name,
        top_margin: preset.top_margin,
        bottom_margin: preset.bottom_margin,
        description: preset.description ?? '',
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ ...RENDER_PRESET_DEFAULTS, crop_ratio: undefined });
    }
    setPreviewContentId(readStoredPreviewContentId());
  }, [open, preset, form]);

  const handlePreviewContentIdChange = (next: number | null) => {
    setPreviewContentId(next);
    try {
      if (next && next > 0) {
        window.localStorage.setItem(PREVIEW_CONTENT_STORAGE_KEY, String(next));
      } else {
        window.localStorage.removeItem(PREVIEW_CONTENT_STORAGE_KEY);
      }
    } catch {
      // 저장에 실패해도 이번 세션 미리보기는 그대로 동작한다.
    }
  };

  /**
   * Map `errors.<field>` onto the matching form fields; anything without a field
   * (e.g. platform_id, which has no input) falls back to a toast.
   */
  const applyServerErrors = (error: unknown, fallback: string): void => {
    const fieldErrors = extractPresetFieldErrors(error);
    const entries = Object.entries(fieldErrors);
    const matched = entries.filter(([field]) => isFormFieldName(field));

    if (matched.length > 0) {
      form.setFields(
        matched.map(([name, errors]) => ({ name: name as keyof RenderPresetFormValues, errors }))
      );
    }

    const unmatched = entries.filter(([field]) => !isFormFieldName(field));
    if (matched.length === 0 || unmatched.length > 0) {
      message.error(formatPresetErrorMessage(error, fallback));
    }
  };

  const handleSubmit = async () => {
    let values: RenderPresetFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const payload: ShortsRenderPresetRequest = {
      name: values.name.trim(),
      font: values.font,
      // Both colours always go together. The backend compares the incoming font_color
      // against the *effective* bg_color, so omitting one and sending the other can trip
      // the "must differ" check against a default the operator never chose.
      font_color: values.font_color,
      bg_color: values.bg_color,
      size_episode: values.size_episode,
      size_date: values.size_date,
      size_program: values.size_program,
      size_subtitle: values.size_subtitle,
      subtitle_background: values.subtitle_background,
      subtitle_bg_opacity: values.subtitle_bg_opacity,
      fit_mode: values.fit_mode,
      // Server nulls crop_ratio on `fit` anyway; sending null keeps the response
      // predictable instead of leaving a stale ratio in the payload.
      crop_ratio: values.fit_mode === 'crop' ? (values.crop_ratio ?? null) : null,
      crop_position: values.crop_position,
      show_episode_name: values.show_episode_name,
      show_on_air_date: values.show_on_air_date,
      show_program_name: values.show_program_name,
      top_margin: values.top_margin,
      bottom_margin: values.bottom_margin,
      description: values.description ?? '',
    };

    setSubmitting(true);
    try {
      if (isEdit && preset) {
        await shortsRenderPresetService.updatePreset(preset.id, payload);
        message.success('Render preset updated');
      } else {
        // platform_id is write-only and required on create only.
        const platformId = accountStore.getAccount()?.platform?.id;
        if (!platformId) {
          message.error('Cannot determine the current platform. Please reload and try again.');
          return;
        }
        await shortsRenderPresetService.createPreset({ ...payload, platform_id: platformId });
        message.success('Render preset created');
      }
      onSaved();
    } catch (error) {
      applyServerErrors(
        error,
        isEdit ? 'Failed to update render preset' : 'Failed to create render preset'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const outputSize =
    isCrop && cropRatio ? RENDER_PRESET_OUTPUT_SIZES[cropRatio] : RENDER_PRESET_FIT_OUTPUT_SIZE;

  /**
   * 저장 전 값으로 미리보기. 서버가 400 을 낼 조합이면 아예 호출하지 않고 이유를 적는다 —
   * 편집 도중엔 폼이 잠깐 잘못된 상태를 지나가는 게 정상이라, 그때마다 빨간 에러를
   * 띄우면 읽을 수 없는 화면이 된다.
   */
  const previewBlockedReason = ((): string | null => {
    if (!previewContentId) return '미리볼 콘텐츠(에피소드) ID를 입력하세요.';
    if (!watched?.name?.trim()) return '이름을 입력하면 미리보기가 그려집니다.';
    // 프리뷰도 CRUD serializer 를 상속해 같은 검증을 돌린다.
    if (watched.font_color && watched.font_color === watched.bg_color) {
      return '글자색과 배경색이 같습니다.';
    }
    if (watched.fit_mode === 'crop' && !watched.crop_ratio) return 'Crop 비율을 선택하세요.';
    return null;
  })();

  const previewRequest: RenderPreviewRequest | null =
    previewBlockedReason || !watched || !previewContentId
      ? null
      : {
          content_id: previewContentId,
          preset: {
            // name 은 서버에서 여전히 required — Values serializer 가 CRUD serializer 를
            // 상속해서 platform_id 만 optional 로 풀렸다.
            name: (watched.name ?? '').trim(),
            font: watched.font,
            font_color: watched.font_color,
            bg_color: watched.bg_color,
            size_episode: watched.size_episode,
            size_date: watched.size_date,
            size_program: watched.size_program,
            size_subtitle: watched.size_subtitle,
            subtitle_background: watched.subtitle_background,
            subtitle_bg_opacity: watched.subtitle_bg_opacity,
            fit_mode: watched.fit_mode,
            crop_ratio: watched.fit_mode === 'crop' ? (watched.crop_ratio ?? null) : null,
            crop_position: watched.crop_position,
            show_episode_name: watched.show_episode_name,
            show_on_air_date: watched.show_on_air_date,
            show_program_name: watched.show_program_name,
            top_margin: watched.top_margin,
            bottom_margin: watched.bottom_margin,
          },
        };

  return (
    <Modal
      title={isEdit ? 'Edit Shorts Render Preset' : 'New Shorts Render Preset'}
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      okText="Save"
      confirmLoading={submitting}
      destroyOnClose
      width={1100}
    >
      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Form form={form} layout="vertical" preserve={false}>
            <Form.Item
              label="Name"
              name="name"
              rules={[
                { required: true, message: 'Please enter a preset name' },
                { max: 255, message: 'Name must be 255 characters or fewer' },
              ]}
            >
              <Input placeholder="e.g. 스타일1" />
            </Form.Item>

            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item
                label="Font"
                name="font"
                style={{ flex: 2 }}
                extra={
                  'Only Noto Sans CJK KR is installed on the render server. The others are ' +
                  'selectable on the backend but fail the Batch job.'
                }
              >
                <Select options={RENDER_PRESET_FONT_OPTIONS} />
              </Form.Item>
              <Form.Item
                label="Font Color"
                name="font_color"
                style={{ flex: 1 }}
                // Mirrors the backend check so it surfaces before saving.
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || value !== getFieldValue('bg_color')) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Font color must differ from background'));
                    },
                  }),
                ]}
              >
                <Select
                  options={RENDER_PRESET_COLOR_OPTIONS}
                  onChange={() => form.validateFields(['font_color'])}
                />
              </Form.Item>
              <Form.Item label="Background Color" name="bg_color" style={{ flex: 1 }}>
                <Select
                  options={RENDER_PRESET_COLOR_OPTIONS}
                  // Re-run the paired rule that lives on font_color.
                  onChange={() => form.validateFields(['font_color'])}
                />
              </Form.Item>
            </div>

            <div style={{ display: 'flex', gap: '16px' }}>
              {SIZE_FIELDS.map(field => (
                <Form.Item
                  key={field.name}
                  label={field.label}
                  name={field.name}
                  style={{ flex: 1 }}
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={RENDER_PRESET_SIZE_MIN}
                    max={RENDER_PRESET_SIZE_MAX}
                    step={2}
                    precision={0}
                  />
                </Form.Item>
              ))}
            </div>

            {/* 체크 = box, 해제 = outline. 서버는 enum 이지만 운영자에겐 켜고 끄는 스위치 하나가
            맞는 모델이라 valuePropName 대신 값 변환을 직접 건다. */}
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
              <Form.Item label="자막 배경 박스" name="subtitle_background" style={{ flex: 1 }}>
                <SubtitleBackgroundToggle />
              </Form.Item>
              {hasSubtitleBox && (
                <Form.Item
                  label="배경 불투명도 (%)"
                  name="subtitle_bg_opacity"
                  style={{ flex: 2 }}
                  extra="글자색의 반대색으로 깔립니다 (흰 글자 → 검정 박스)."
                >
                  <Slider
                    min={RENDER_PRESET_OPACITY_MIN}
                    max={RENDER_PRESET_OPACITY_MAX}
                    marks={{ 0: '0', 60: '60', 100: '100' }}
                  />
                </Form.Item>
              )}
            </div>

            <Form.Item label="Fit Mode" name="fit_mode" extra={`Output: ${outputSize}`}>
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                options={RENDER_PRESET_FIT_MODE_OPTIONS}
              />
            </Form.Item>

            {/* crop_ratio and crop_position carry no meaning on `fit` — the backend ignores
            both and clears the ratio, so hiding them avoids a setting that silently
            does nothing. */}
            {isCrop && (
              <div style={{ display: 'flex', gap: '16px' }}>
                <Form.Item
                  label="Crop Ratio"
                  name="crop_ratio"
                  style={{ flex: 1 }}
                  rules={[
                    { required: true, message: 'Crop ratio is required when fit mode is Crop' },
                  ]}
                >
                  <Select placeholder="Select ratio" options={RENDER_PRESET_CROP_RATIO_OPTIONS} />
                </Form.Item>
                <Form.Item label="Crop Position" name="crop_position" style={{ flex: 1 }}>
                  <Select options={RENDER_PRESET_CROP_POSITION_OPTIONS} />
                </Form.Item>
              </div>
            )}

            {isCrop && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: '16px' }}
                message="Crop leaves no margin, so labels are drawn on top of the video."
                description={
                  'Background color has no visible effect, and labels get a 3px outline ' +
                  'in the opposite color.'
                }
              />
            )}

            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item
                label="Episode name (top)"
                name="show_episode_name"
                valuePropName="checked"
                style={{ flex: 1 }}
              >
                <Switch />
              </Form.Item>
              <Form.Item
                label="On-air date (top)"
                name="show_on_air_date"
                valuePropName="checked"
                style={{ flex: 1 }}
              >
                <Switch />
              </Form.Item>
              <Form.Item
                label="Program name (bottom)"
                name="show_program_name"
                valuePropName="checked"
                style={{ flex: 1 }}
              >
                <Switch />
              </Form.Item>
            </div>

            <div style={{ display: 'flex', gap: '16px' }}>
              <Form.Item
                label="Top Margin (px)"
                name="top_margin"
                style={{ flex: 1 }}
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={RENDER_PRESET_MARGIN_MIN}
                  max={RENDER_PRESET_MARGIN_MAX}
                  precision={0}
                />
              </Form.Item>
              <Form.Item
                label="Bottom Margin (px)"
                name="bottom_margin"
                style={{ flex: 1 }}
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={RENDER_PRESET_MARGIN_MIN}
                  max={RENDER_PRESET_MARGIN_MAX}
                  precision={0}
                />
              </Form.Item>
            </div>

            <Form.Item label="Description" name="description">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Form>
        </div>

        <div style={{ width: 320, flexShrink: 0 }}>
          <div style={{ fontWeight: 500, marginBottom: 8 }}>미리보기</div>
          <RenderPreviewPane
            request={previewRequest}
            blockedReason={previewBlockedReason ?? undefined}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              precision={0}
              value={previewContentId}
              onChange={value => handlePreviewContentIdChange(value ?? null)}
              placeholder="에피소드 콘텐츠 ID"
            />
          </RenderPreviewPane>
          <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
            서버가 실제 변환과 같은 코드로 그린 정지 프레임입니다. 영상이 없는 콘텐츠는 대표 썸네일
            위에 그립니다. 자막이 있으면 첫 대사가 보이는 지점을 자동으로 고릅니다.
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ShortsRenderPresetFormModal;
